import { h, Component, Host, Prop, Element, Watch, Listen } from '@stencil/core';

@Component({
  tag: 'pk-accordion-group',
  styleUrl: './scss/pk-accordion-group.scss',
  shadow: true,
})
export class PkAccordionGroup {
  // Own Properties
  // ====================

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkAccordionGroupElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  // Public Property API
  // ========================

  /** All items are disabled */
  @Prop() disabled: boolean = false;

  /**  Allow multiple open items at a time */
  @Prop() multipleOpen: boolean = false;

  // Prop lifecycle events
  // =======================

  @Watch('disabled')
  handleDisable() {
    this.syncChildren();
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  connectedCallback() {
    this.syncChildren();
  }

  // Listeners
  // ==============

  @Listen('pkToggle')
  handleItemToggle(event: CustomEvent<{ expanded: boolean }>) {
    // Extracting target and detail from the event
    const element: EventTarget = event.target;
    const { expanded }: { expanded: boolean } = event.detail;

    if (expanded && !this.multipleOpen) {
      // Collapse all other items if they are not disabled
      this.collapseItems
        .filter((item) => item !== element && !item.disabled)
        .forEach((item) => {
          item.collapse();
        });
    }
  }

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private get collapseItems() {
    return Array.from(this.el.querySelectorAll('pk-accordion-item') ?? []);
  }

  private syncChildren() {
    for (const item of this.collapseItems) {
      item.disabled = this.disabled;
    }
  }

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    return (
      <Host>
        <slot />
      </Host>
    );
  }
}
